import { useMemo } from 'react';
import {
  qAppConfig,
  getLaboratorySettings,
  resolveLaboratoryCapabilities,
  type AppConfigRow,
  type LaboratoryCapabilities,
  type LaboratoryPatientWorkspaceAccess,
  type LaboratorySurface,
} from '@helios/data-access';
import { usePermissions } from '@helios/auth-firebase-web';
import { usePowerSyncQuery } from '../adapters/powersync';
import { useLaboratoryModuleEnabled } from './useClinicMainModules';
import { PERM } from '../auth/permissionMap';

/**
 * PRD.305 — reactive Laboratory Settings from the singleton app_config row
 * (`extra_json.laboratory`). This is the ONE place the raw JSON is read, so
 * Laboratory Settings, Patient Labs and Consultation Labs can never disagree
 * about the clinic's mode.
 */
export function useLaboratorySettings(): {
  patientWorkspaceAccess: LaboratoryPatientWorkspaceAccess;
} {
  const { data } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  return useMemo(() => getLaboratorySettings(data?.[0] ?? null), [data]);
}

/**
 * The effective capability set for a laboratory surface:
 *
 *   module availability ∩ Laboratory Settings mode ∩ RBAC
 *
 * PRD §8.2 — configuration is not authorization. Choosing Full Management can
 * only lift restrictions the MODE imposed; it can never grant an action the
 * user has no permission for, because every operational capability is ANDed
 * with its permission inside `resolveLaboratoryCapabilities`.
 *
 * Permissions are read only once they have RESOLVED. `canAccess` fails closed
 * while loading, so evaluating it early would briefly blank a surface the user
 * can actually use — the same reason EncounterDetailPage waits on
 * `permissionStatus === 'ready'` before applying RBAC to its tab set.
 */
export function useLaboratoryCapabilities(surface: LaboratorySurface): LaboratoryCapabilities & {
  /** False until permissions resolve — hold gated UI until then. */
  ready: boolean;
} {
  const moduleEnabled = useLaboratoryModuleEnabled();
  const { patientWorkspaceAccess } = useLaboratorySettings();
  const { canAccess, status } = usePermissions();

  return useMemo(() => {
    const ready = status === 'ready';
    const caps = resolveLaboratoryCapabilities({
      surface,
      moduleEnabled,
      mode: patientWorkspaceAccess,
      permissions: {
        viewOrders: canAccess(PERM.labOrders),
        createOrders: canAccess(PERM.labOrdersCreate),
        manageOrders: canAccess(PERM.labOrdersManage),
        viewResults: canAccess(PERM.labResults),
        manageWorkflow: canAccess(PERM.labResultsUpload),
        publishResults: canAccess(PERM.labResultsPublish),
      },
    });
    return { ...caps, ready };
  }, [surface, moduleEnabled, patientWorkspaceAccess, canAccess, status]);
}
